import { makeAutoObservable, runInAction } from 'mobx'
import { CanceledError } from 'axios'
import { debounce } from 'lodash'
import { RequestCancelAxios } from 'shared/api/lib'
import { logger } from 'shared/lib'
import {
  type IResponseCallHistoryCall,
  type IFiltersList,
  type IParamsGetCallHistoryStatistic,
  type IResponseGetCallHistoryStatistic,
} from 'entities/CallHistory/api/types'
import { CallHistoryApi } from 'entities/CallHistory/api/callHistory'
import { type IReadMessage } from 'entities/Conversation/api/types'
import { CallFilter } from 'pages/conversations/pages/calls/ui/CallHistoryFilters/store/CallFilter'
import { type ICallFilter } from 'pages/conversations/pages/calls/ui/CallHistoryFilters/store/type'

// Reconcile the statistics counts at most once per window to avoid hammering the
// heavy call-history statistics query when many read events arrive in a burst.
const SYNC_FILTERS_DEBOUNCE_MS = 5000

const filters: ICallFilter[] = [
  { type: 'all-calls', outcome: null },
  { type: 'inbound', outcome: 'answered' },
  { type: 'inbound', outcome: 'missed' },
  { type: 'inbound', outcome: 'voicemail' },
  { type: 'inbound', outcome: null, source: 'ai_calling_agent' },
  { type: 'outbound', outcome: 'answered' },
  { type: 'outbound', outcome: 'no_answer' },
  { type: 'outbound', outcome: 'voicemail' },
]

export class CallHistoryFiltersStore {
  private _loading = false
  private _activeFilterKey = 'all-calls'
  private _filtersMap: Map<string, CallFilter> = new Map(
    filters.map((item) => {
      const filter = new CallFilter(item)
      return [filter.id, filter]
    })
  )
  private _cancelRequest = new RequestCancelAxios()
  private _syncInFlight = false
  private _pendingSyncParams: IParamsGetCallHistoryStatistic | null = null
  private _debouncedSyncFilters: ReturnType<typeof debounce>

  constructor() {
    makeAutoObservable(this)

    this._debouncedSyncFilters = debounce((params: IParamsGetCallHistoryStatistic) => {
      this._runSyncFilters(params)
    }, SYNC_FILTERS_DEBOUNCE_MS)
  }

  get loading() {
    return this._loading
  }

  get activeFilterKey() {
    return this._activeFilterKey
  }

  get activeFilter() {
    return this._filtersMap.get(this._activeFilterKey)
  }

  get allCallsFilter() {
    return this._filtersMap.get('all-calls')
  }

  get filters() {
    return Array.from(this._filtersMap.values())
  }

  get inboundFilters() {
    return this.filters.filter((item) => item.type === 'inbound')
  }

  get outboundFilters() {
    return this.filters.filter((item) => item.type === 'outbound')
  }

  get isActiveAllCalls() {
    return this.activeFilter?.type === 'all-calls'
  }

  setActiveFilter = (value: string) => {
    this._activeFilterKey = value
  }

  get filtersList() {
    if (this.activeFilter && !this.isActiveAllCalls) {
      return [
        {
          filters: [
            { key: 'call.type', operator: 'equals', value: this.activeFilter.type },
            ...(this.activeFilter.outcome
              ? [{ key: 'call.outcome', operator: 'equals', value: this.activeFilter.outcome }]
              : []),
            ...(this.activeFilter.source
              ? [{ key: 'message.source', operator: 'equals', value: this.activeFilter.source }]
              : []),
          ],
        },
      ] as IFiltersList[]
    }

    return []
  }

  // The statistics response no longer reports a total count, so we can no longer
  // know upfront whether the org has any calls - default to true and let the
  // call-history list request (loaded concurrently) be the source of truth.
  get hasCalls() {
    return true
  }

  setData = ({ data }: IResponseGetCallHistoryStatistic) => {
    const { unread_count, groups } = data

    this.allCallsFilter?.setUnreadCount(unread_count)

    Object.entries(groups).forEach(([type, { groups }]) => {
      Object.entries(groups).forEach(([outcome, { unread_count }]) => {
        const filter = this._filtersMap.get(`${type}.${outcome}`)
        if (filter) {
          filter.setUnreadCount(unread_count)
        }
      })
    })
  }

  loadData = async (params: IParamsGetCallHistoryStatistic) => {
    this.reset()

    try {
      this._cancelRequest.init()

      runInAction(() => {
        this._loading = true
      })

      const { data } = await CallHistoryApi.getCallHistoryStatistic(params, {
        cancelToken: this._cancelRequest.token,
      })

      this.setData(data)

      return data
    } catch (e) {
      runInAction(() => {
        this._loading = e instanceof CanceledError
      })
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  // Fire-and-forget reconcile of the filter counts with the server. Debounced and
  // guarded against overlapping requests so a burst of websocket read events cannot
  // stampede the heavy call-history statistics query (regressed in SWR-23600).
  syncFilters = (params: IParamsGetCallHistoryStatistic) => {
    this._debouncedSyncFilters(params)
  }

  private _runSyncFilters = async (params: IParamsGetCallHistoryStatistic) => {
    if (this._syncInFlight) {
      this._pendingSyncParams = params
      return
    }

    try {
      runInAction(() => {
        this._syncInFlight = true
      })
      this._cancelRequest.init()

      const { data } = await CallHistoryApi.getCallHistoryStatistic(params, {
        cancelToken: this._cancelRequest.token,
      })

      this.setData(data)
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._syncInFlight = false
      })

      if (this._pendingSyncParams) {
        const pendingParams = this._pendingSyncParams
        this._pendingSyncParams = null
        this._runSyncFilters(pendingParams)
      }
    }
  }

  increaseCount = ({ type, outcome, source }: IResponseCallHistoryCall) => {
    this.allCallsFilter?.increaseUnreadCount()
    if (source === 'ai_calling_agent' && type) {
      this._filtersMap.get(`${type}.${source}`)?.increaseUnreadCount()
    }

    const filter = this._filtersMap.get(`${type}.${outcome}`)
    if (filter) {
      filter.increaseUnreadCount()
    }
  }

  decreaseCount = (data: IReadMessage[]) => {
    data.forEach(({ type, direction, status, source }) => {
      if (type === 'call') {
        this.allCallsFilter?.decreaseUnreadCount()

        if (source === 'ai_calling_agent' && direction) {
          this._filtersMap.get(`${direction}.${source}`)?.decreaseUnreadCount()
        }

        const filter = this._filtersMap.get(`${direction}.${status}`)
        if (filter) {
          filter.decreaseUnreadCount()
        }
      }
    })
  }

  reset = () => {
    this._debouncedSyncFilters.cancel()
    this._pendingSyncParams = null
    this._filtersMap.forEach((filter) => filter.reset())
    this._activeFilterKey = 'all-calls'
    this._cancelRequest.reset()
  }
}
